import { Box, VStack, Text } from '@wegroup/design-system';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ThemeContext } from 'styled-components';
import RiskObjectIcon from '../../../../../common/src/components/RiskObjectIcon';
import { chakraCustomScrollBar } from '../../../../../common/src/utils/styledComponentsUtils';
import { useQuotes } from '../../../hooks/useQuoteQueries';
import { RootState } from '../../../store/redux/reducers/rootReducer';
import { getOfferState, OfferState } from '../../../services/offersService';
import { getTextForRiskObject } from '../../../services/productFactoryService';
import { toEuro } from '../../../utils/stringUtils';
import SelectedOfferSummaryTotal from './SelectedOfferSummaryTotal';
import SingleSelectedOffer from './SingleSelectedOffer';

const SelectedOffersSummary: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { t } = useTranslation();

  const theme = useContext(ThemeContext);

  const selectedOffers = useSelector(
    (state: RootState) => state.offers.selectedOffers,
  );
  const preferredOffers = useSelector(
    (state: RootState) => state.offers.preferredOffers,
  );

  const hasAllPreferredOffer = Object.keys(selectedOffers).every(
    (riskObject) => !!preferredOffers[riskObject],
  );

  const { data: preferredOffersData, isFetched } = useQuotes(preferredOffers);

  const getSumOfPreferredOffers = () =>
    preferredOffersData.reduce((prev, quote) => {
      const price = quote?.premium.total_premium;
      if (typeof quote?.premium.total_premium === 'string')
        parseFloat(quote?.premium.total_premium);
      switch (quote?.details.payment_interval?.toLowerCase()) {
        case 'annual':
          return prev + price;
        case 'half_annual':
          return prev + price * 2;
        case 'quarterly':
          return prev + price * 4;
        case 'monthly':
          return prev + price * 12;
        default:
          return prev;
      }
    }, 0);

  const isAnySelectedOfferFailed = preferredOffersData.some(
    (quote) => getOfferState(quote) === OfferState.ERROR,
  );

  const shouldShowPrice =
    isFetched && !isAnySelectedOfferFailed && hasAllPreferredOffer;

  if (
    Object.keys(selectedOffers).some(
      (riskObject) => selectedOffers[riskObject]?.length > 0,
    )
  )
    return (
      <VStack
        width="100%"
        divider={<Box width="100%" border="1px" borderColor="gray.50" />}
        spacing={4}
        overflowY={{ laptop: 'auto' }}
        overflowX="hidden"
        p={6}
        maxH="100%"
        sx={chakraCustomScrollBar}
        borderRadius="6px 0 0 6px"
        bg="white"
      >
        {Object.keys(selectedOffers).map((riskObject) => {
          return (
            <SingleSelectedOffer
              key={`selected_box_preferred_offer_${riskObject}`}
              Icon={
                <RiskObjectIcon
                  riskObject={riskObject}
                  color={theme.gray[200]}
                />
              }
              title={getTextForRiskObject(riskObject)}
              riskObject={riskObject}
              preferredOfferId={preferredOffers[riskObject]}
              multiple={selectedOffers[riskObject].length > 1}
            />
          );
        })}
        <VStack width="100%" spacing={3}>
          <SelectedOfferSummaryTotal
            price={shouldShowPrice ? toEuro(getSumOfPreferredOffers()) : '€ -'}
            interval={hasAllPreferredOffer ? t('ANNUAL') : ''}
          />
        </VStack>
      </VStack>
    );

  return (
    <VStack p={6} width="100%" borderRadius="6px 0 0 6px" bg="white">
      <Text>{t('No offers selected')}</Text>
    </VStack>
  );
};

export default SelectedOffersSummary;
